import { genUiT } from '../../shared/i18n_core.js';
import type { RoomBed, RoomIdentity, RoomTier } from './hotel_room_taxonomy.js';

type TKey = Parameters<typeof genUiT>[1];

function bedLabel(bed: RoomBed, locale: string): string {
  const family = bed.family ?? 'UNKNOWN';
  const plural = bed.count === 1 ? 'one' : 'other';
  return genUiT(locale, `roomTaxonomy.beds.${family}_${plural}`, { count: bed.count });
}

/** Resíduo cru vira Title Case simples — o token chega normalizado em CAIXA ALTA. */
function rawTitle(raw: string): string {
  return raw
    .split(' ')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function buildHead(
  identity: RoomIdentity,
  locale: string,
  t: (key: TKey, values?: Record<string, string | number>) => string,
): string {
  const typeWord = t(`roomTaxonomy.type.${identity.type ?? 'QUARTO'}`);
  const tiers = [identity.tier, identity.tier2].filter(Boolean) as RoomTier[];
  const tierWords = tiers.map((tier) => t(`roomTaxonomy.tier.${tier}`));
  if (identity.type === undefined && tierWords.length > 0 && locale.startsWith('en')) {
    return `${tierWords.join(' ')} room`;
  }
  return [typeWord, ...tierWords].join(' ');
}

function addBedsToTerms(terms: string[], identity: RoomIdentity, locale: string): void {
  for (const bed of identity.beds ?? []) {
    terms.push(bedLabel(bed, locale));
  }
}

function addBedroomsToTerms(
  terms: string[],
  identity: RoomIdentity,
  t: (key: TKey, values?: Record<string, string | number>) => string,
): void {
  if (identity.bedrooms !== undefined) {
    const plural = identity.bedrooms === 1 ? 'one' : 'other';
    terms.push(t(`roomTaxonomy.bedrooms_${plural}`, { count: identity.bedrooms }));
  }
}

function addCapacityToTerms(
  terms: string[],
  identity: RoomIdentity,
  t: (key: TKey, values?: Record<string, string | number>) => string,
): void {
  const hideCapacity = identity.capacity === 1 && (identity.beds?.length ?? 0) > 0;
  if (identity.capacity !== undefined && !hideCapacity) {
    terms.push(t('roomTaxonomy.capacity', { count: identity.capacity }));
  }
}

function addQualifiersToTerms(
  terms: string[],
  identity: RoomIdentity,
  t: (key: TKey, values?: Record<string, string | number>) => string,
): void {
  for (const qualifier of identity.qualifiers ?? []) {
    if (qualifier.code) {
      terms.push(t(`roomTaxonomy.qualifier.${qualifier.code}`));
    } else if (qualifier.raw) {
      terms.push(rawTitle(qualifier.raw));
    }
  }
}

/**
 * O rótulo do cartão a partir dos TOKENS (o servidor nunca escreve palavras).
 * Cabeça = tipo + degrau(s) em uma frase; o resto entra como termos " · ".
 * Capacidade 1 com cama presente é ruído e some (o eixo continua na CHAVE).
 * "Quarto" (tipo default) só abre a frase quando há degrau ou nada mais — em
 * en, a ordem natural inverte ("Standard room"), por isso a chave de tipo é
 * traduzida e a montagem testa o locale só pelo que o dicionário devolve.
 */
export function roomIdentityLabel(identity: RoomIdentity, locale: string): string {
  const t = (key: TKey, values?: Record<string, string | number>) => genUiT(locale, key, values);
  const head = buildHead(identity, locale, t);
  const terms: string[] = [head];

  addBedsToTerms(terms, identity, locale);
  addBedroomsToTerms(terms, identity, t);
  addCapacityToTerms(terms, identity, t);
  addQualifiersToTerms(terms, identity, t);

  return terms.join(' · ');
}
